'use client'

import { StateCreator } from 'zustand'
import { type StoreState } from '.'

const MAX_POSITION_NAME_LENGTH = 18

// Record<`${ownerAddress}:${subAccountId}`, customName>
type PositionNamesMap = Record<string, string>

function makeKey(owner: string, subAccountId: number): string {
  return `${owner.toLowerCase()}:${subAccountId}`
}

export interface PositionNames {
  names: PositionNamesMap
  setPositionName: (owner: string, subAccountId: number, name: string) => void
  clearPositionName: (owner: string, subAccountId: number) => void
  getPositionName: (owner: string | undefined, subAccountId: number) => string | undefined
}

export interface PositionNamesSlice {
  positionNames: PositionNames
}

export const initPositionNamesSlice: StateCreator<StoreState, [], [], PositionNamesSlice> = (set, get) => ({
  positionNames: {
    names: {},
    setPositionName: (owner: string, subAccountId: number, name: string) => {
      const trimmed = name.trim().slice(0, MAX_POSITION_NAME_LENGTH)
      if (!trimmed) return
      set((state) => ({
        positionNames: {
          ...state.positionNames,
          names: {
            ...state.positionNames.names,
            [makeKey(owner, subAccountId)]: trimmed,
          },
        },
      }))
    },
    clearPositionName: (owner: string, subAccountId: number) =>
      set((state) => {
        const { [makeKey(owner, subAccountId)]: _, ...rest } = state.positionNames.names
        return {
          positionNames: {
            ...state.positionNames,
            names: rest,
          },
        }
      }),
    getPositionName: (owner: string | undefined, subAccountId: number) => {
      if (!owner) return undefined
      return get().positionNames.names[makeKey(owner, subAccountId)]
    },
  },
})

export interface PersistedPositionNamesSlice {
  positionNames: {
    names: PositionNamesMap
  }
}

export function persistPositionNamesSlice(state: StoreState): PersistedPositionNamesSlice {
  return {
    positionNames: {
      names: state.positionNames.names,
    },
  }
}
